import { Fragment, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Card,
  Chip,
  Collapse,
  Divider,
  ListItemText,
  MenuItem,
  MenuList,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { ThemeProvider, alpha } from '@mui/material/styles';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import { workSansHeadings } from '@/designs-dev/explore/exploreTheme';
import EditableText from '@/designs-dev/explore/EditableText';
import {
  explorationRoute,
  projectForSpec,
} from '@/designs-dev/explore/explorations';
import {
  EditingProvider,
  frameDirection,
  useEditing,
} from '@/designs-dev/explore/editing';
import { writeClipboard } from '@/designs-dev/loft/command';

import ExploreScreen, {
  LazyMount,
  SCALE,
  SIZE_LABEL,
  oneStepDown,
  type FrameSize,
  type ScreenVariant,
} from '@/designs-dev/explore/ExploreScreen';
import {
  type ExplorationSpec,
  type Frame,
  type RoughCopy,
  type Section,
} from '@/designs-dev/explore/types';
import { ConceptTag } from '@/designs-dev/loft/conceptTag';

const SectionTitle = ({ label }: { label: string }) => (
  <Typography variant='overline' color='text.secondary'>
    {label}
  </Typography>
);

// What stands in for a frame nobody has drawn yet. Rendered inside a real
// frame so an unsketched one occupies exactly the footprint of a sketched one
// — the gap is legible at a glance, and the page doesn't reflow as frames get
// filled in.
const NotSketched = () => (
  <Stack
    sx={{
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'custom.neutral.50',
    }}
    spacing={2}
  >
    <Box sx={{ color: 'custom.neutral.300', display: 'flex' }}>
      <Icon icon={'pen-field' as never} fontSize={64} />
    </Box>
    <Typography variant='h3' color='custom.neutral.400'>
      Not sketched yet
    </Typography>
  </Stack>
);

// Illustrative language. A caption annotates the frame from outside; lines are
// copy the screen itself would show; an exchange is a worked back-and-forth.
const Copy = ({
  copy,
  path,
  where,
}: {
  copy: RoughCopy;
  path: string;
  where: string;
}) => (
  <Stack spacing={1.5}>
    {copy.caption && (
      <EditableText
        sx={{ fontStyle: 'italic' }}
        variant='body2'
        color='text.secondary'
        path={`${path}.caption`}
        what={`${where} — rough copy caption`}
        value={copy.caption}
      />
    )}

    {copy.lines && (
      <Stack spacing={1}>
        {copy.lines.map((line, i) => (
          <Box
            sx={{
              px: 2,
              py: 1.25,
              bgcolor: 'custom.neutral.50',
              border: 1,
              borderColor: 'custom.neutral.200',
              borderRadius: `${radius.md}px`,
            }}
            key={i}
          >
            <EditableText
              variant='body2'
              path={`${path}.lines[${i}]`}
              what={`${where} — rough copy line ${i + 1}`}
              value={line}
            />
          </Box>
        ))}
      </Stack>
    )}

    {copy.exchange && (
      <Stack spacing={1}>
        {copy.exchange.map((turn, i) => {
          const isUs = turn.from === 'us';
          return (
            <Box
              sx={{
                alignSelf: isUs ? 'flex-start' : 'flex-end',
                maxWidth: '88%',
                px: 2,
                py: 1.25,
                bgcolor: isUs ? 'background.paper' : 'custom.coral.600',
                border: isUs ? 1 : 0,
                borderColor: 'custom.neutral.200',
                borderRadius: `${radius.lg}px`,
              }}
              key={i}
            >
              <EditableText
                variant='body2'
                color={isUs ? 'text.primary' : 'common.white'}
                path={`${path}.exchange[${i}].text`}
                what={`${where} — ${isUs ? 'our' : 'their'} line ${i + 1} of the exchange`}
                value={turn.text}
              />
            </Box>
          );
        })}
      </Stack>
    )}
  </Stack>
);

// A quiet inline disclosure. Deliberately not an Accordion — the page already
// carries a lot of surfaces, and Accordion's chrome would add weight to the
// very thing being decluttered.
const Disclosure = ({
  label,
  open,
  onToggle,
  children,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) => (
  <Stack sx={{ alignItems: 'flex-start' }} spacing={1.5}>
    <Button
      ariaLabel={label}
      variant='link'
      size='sm'
      isNoPadding
      onClick={onToggle}
      iconRight={(open ? 'chevron-up' : 'chevron-down') as never}
    >
      {label}
    </Button>
    <Collapse in={open} unmountOnExit>
      {children}
    </Collapse>
  </Stack>
);

// The written half of a frame, cut to what the sketch can't say for itself.
//
// Visible: the name, the trade (rivals only), the prominent line — `chasing`
// where there's a question, `inTheFrame` where there isn't — and the user
// story. What's left goes behind a disclosure on purpose: `inTheFrame`
// describes the thing you are already looking at, and `copy` repeats copy
// rendered inside the frame. Keeping them on the page by default buried the
// sketches in prose.
const FrameBrief = ({
  frame,
  path,
  size,
}: {
  frame: Frame;
  path: string;
  size: FrameSize;
}) => {
  const where = `Frame ${frame.id} · ${frame.name}`;
  // Twice the frame it sits beside, and no wider. Left to fill the row the
  // lines run long enough to lose your place between them — a note pinned to
  // a sketch should read like a note, not a column of body copy. Scales with
  // the size control so the relationship holds at every zoom.
  const noteWidth = 2 * 375 * SCALE[size].phone;

  return (
    <Box
      sx={{ flex: 1, minWidth: Math.min(380, noteWidth), maxWidth: noteWidth }}
    >
      <Stack spacing={2}>
        <Stack sx={{ alignItems: 'baseline' }} direction='row' spacing={1.5}>
          <Typography variant='overline' color='text.secondary'>
            {frame.id}
          </Typography>
          <EditableText
            variant='h4'
            path={`${path}.name`}
            what={`${where} — name`}
            value={frame.name}
          />
          {frame.maybe && (
            <Chip size='small' variant='outlined' label='Explicit maybe' />
          )}
        </Stack>

        {(frame.optimises || frame.costs) && (
          <Stack spacing={1}>
            {frame.optimises && (
              <Stack
                sx={{ alignItems: 'flex-start' }}
                direction='row'
                spacing={1.5}
              >
                <Box
                  sx={{
                    color: 'custom.limeGreen.800',
                    display: 'flex',
                    pt: '3px',
                  }}
                >
                  <Icon icon={'arrow-circle-up' as never} fontSize={13} />
                </Box>
                <Stack
                  sx={{ flex: 1, minWidth: 0 }}
                  direction='row'
                  spacing={0.5}
                >
                  <Typography
                    sx={{ flexShrink: 0 }}
                    variant='body2'
                    color='text.secondary'
                  >
                    Optimises for ·
                  </Typography>
                  <EditableText
                    variant='body2'
                    path={`${path}.optimises`}
                    what={`${where} — what it optimises for`}
                    value={frame.optimises}
                  />
                </Stack>
              </Stack>
            )}
            {frame.costs && (
              <Stack
                sx={{ alignItems: 'flex-start' }}
                direction='row'
                spacing={1.5}
              >
                <Box
                  sx={{
                    color: 'custom.warmOrange.500',
                    display: 'flex',
                    pt: '3px',
                  }}
                >
                  <Icon icon={'arrow-circle-down' as never} fontSize={13} />
                </Box>
                <Stack
                  sx={{ flex: 1, minWidth: 0 }}
                  direction='row'
                  spacing={0.5}
                >
                  <Typography
                    sx={{ flexShrink: 0 }}
                    variant='body2'
                    color='text.secondary'
                  >
                    Costs ·
                  </Typography>
                  <EditableText
                    variant='body2'
                    path={`${path}.costs`}
                    what={`${where} — what it costs`}
                    value={frame.costs}
                  />
                </Stack>
              </Stack>
            )}
          </Stack>
        )}

        {/* The prominent line. A frame that is chasing something shows the
            question; one that is describing a step shows what's in it, rather
            than leaving the useful sentence folded away in the disclosure. A
            spec can carry neither, in which case the frame is its story and the
            rule is not drawn. */}
        {(frame.chasing || frame.inTheFrame) && (
          <Box sx={{ borderLeft: 2, borderColor: 'custom.coral.600', pl: 2 }}>
            {frame.chasing ? (
              <EditableText
                variant='body2'
                path={`${path}.chasing`}
                what={`${where} — what I'm chasing`}
                value={frame.chasing}
              />
            ) : (
              <EditableText
                variant='body2'
                path={`${path}.inTheFrame`}
                what={`${where} — what's in the frame`}
                value={frame.inTheFrame ?? ''}
              />
            )}
          </Box>
        )}

        {frame.story && (
          <Stack spacing={0.5}>
            <Typography variant='overline' color='text.secondary'>
              As {frame.story.as}
            </Typography>
            <Typography variant='body2'>
              I want {frame.story.iWant}
              {frame.story.soThat && `, so that ${frame.story.soThat}`}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

// A frame that isn't a screen — a data map, or a screen shown beside the real
// artefact it translates into. Gets a plain surface at the width of the brief
// column rather than a phone frame it would misrepresent.
const BareFrame = ({
  frame,
  label,
  caption,
  size,
  overlay,
}: {
  frame: Frame;
  label: string;
  caption?: string;
  size: FrameSize;
  overlay?: React.ReactNode;
}) => (
  <Stack
    sx={{ width: frame.wide ? '100%' : SCALE[size].bare, flexShrink: 0 }}
    spacing={1}
  >
    <Box
      sx={{
        position: 'relative',
        border: 1,
        borderColor: 'custom.neutral.200',
        borderRadius: `${radius.md}px`,
        bgcolor: 'background.default',
        p: 2.5,
        overflow: frame.wide ? 'auto' : 'hidden',
        minHeight: 240,
      }}
      // A `wide` bare frame is here to hold something a phone frame would
      // misrepresent — a table, a map, a ledger — so it must not be clipped.
      // Height is auto either way, so `auto` only ever scrolls sideways, and
      // then only when the content genuinely does not fit.
      // Tall enough that the box holds its place before its interior mounts,
      // so the page doesn't jump as frames come into view.
    >
      <LazyMount keepMounted={frame.interactive}>
        {frame.render ? frame.render() : <NotSketched />}
      </LazyMount>
      {overlay}
    </Box>
    <Box>
      <Typography variant='subtitle2'>{label}</Typography>
      {caption && (
        <Typography
          sx={{ display: 'block', mt: 0.25 }}
          variant='caption'
          color='text.secondary'
        >
          {caption}
        </Typography>
      )}
    </Box>
  </Stack>
);

// The component name behind a frame, read off the element its render returns.
// Creating the element doesn't render it, so this is cheap — and it means the
// direction can name the sketch without every frame authoring a file path.
// Every sketch sets displayName; `name` is the fallback (vite keeps function
// names in the prod build for drill mode, so it survives there too).
const componentName = (frame: Frame): string | undefined => {
  if (!frame.render) return undefined;
  const el = frame.render() as {
    type?: { displayName?: string; name?: string };
  };
  return typeof el?.type === 'function' || typeof el?.type === 'object'
    ? (el.type?.displayName ?? el.type?.name)
    : undefined;
};

// The hover state on a frame: a charcoal scrim over the sketch with the label
// dead centre, so it reads at a glance instead of being a corner chip competing
// with whatever the sketch has in that corner. Sits inside the screen's own
// border, so the caption underneath stays legible.
const CopyOverlay = () => (
  <Stack
    className='frame-copy-overlay'
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      alignItems: 'center',
      justifyContent: 'center',
      opacity: 0,
      transition: 'opacity 120ms',
      pointerEvents: 'none',
      bgcolor: (t) => alpha(t.palette.custom.charcoal, 0.5),
    }}
  >
    <Chip
      label='Copy direction'
      icon={<Icon icon={'copy' as never} fontSize={12} />}
      sx={{ bgcolor: 'background.paper' }}
    />
  </Stack>
);

// The copy affordance for a frame that has its own controls. A corner button
// rather than a full scrim: covering an interactive sketch would make its
// controls unaimable, and clicking through to copy would swallow them.
const CopyCorner = ({ onCopy }: { onCopy: () => void }) => (
  <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
    <Chip
      label='Copy direction'
      size='small'
      icon={<Icon icon={'copy' as never} fontSize={11} />}
      onClick={(e) => {
        e.stopPropagation();
        onCopy();
      }}
      sx={{ bgcolor: 'background.paper' }}
    />
  </Box>
);

// Wraps a frame so clicking it copies a direction — where the sketch and its
// spec live — for the user to paste and then type the actual ask after.
const FrameCopyTarget = ({
  frame,
  path,
  children,
}: {
  frame: Frame;
  path: string;
  // Receives the copy affordance to render inside the frame's own border —
  // a scrim for static sketches, a corner button for interactive ones.
  children: (overlay: React.ReactNode) => React.ReactNode;
}) => {
  const { sourceFile, onCopied, enabled } = useEditing();

  const copy = () => {
    writeClipboard(
      frameDirection({
        sourceFile,
        path,
        id: frame.id,
        name: frame.name,
        component: componentName(frame),
      }),
    );
    onCopied();
  };

  // View mode: the frame is a picture. No click target, no hover state, and
  // an interactive sketch keeps working exactly as it did.
  if (!enabled) return <Box sx={{ flexShrink: 0 }}>{children(null)}</Box>;

  // An interactive sketch keeps its own clicks: no whole-frame handler, and
  // the copy control becomes a corner button the overlay renders instead.
  return frame.interactive ? (
    <Box sx={{ flexShrink: 0 }}>{children(<CopyCorner onCopy={copy} />)}</Box>
  ) : (
    <Box
      onClick={copy}
      sx={{
        flexShrink: 0,
        cursor: 'pointer',
        '&:hover .frame-copy-overlay': { opacity: 1 },
      }}
    >
      {children(<CopyOverlay />)}
    </Box>
  );
};

// `caption` is passed explicitly rather than read off frame.tap: in a sequence
// strip the tap already sits in the connector between frames, so repeating it
// under the mini would say the same thing twice.
const FrameSketch = ({
  frame,
  screen,
  label,
  caption,
  size,
  path,
}: {
  frame: Frame;
  screen: ScreenVariant;
  label: string;
  caption?: string;
  size: FrameSize;
  path: string;
}) => (
  <FrameCopyTarget frame={frame} path={path}>
    {(overlay) =>
      frame.bare ? (
        <BareFrame
          frame={frame}
          label={label}
          caption={caption}
          size={size}
          overlay={overlay}
        />
      ) : (
        <ExploreScreen
          label={label}
          caption={caption}
          variant={frame.screen ?? screen}
          size={size}
          overlay={overlay}
          keepMounted={frame.interactive}
        >
          {frame.render ? frame.render() : <NotSketched />}
        </ExploreScreen>
      )
    }
  </FrameCopyTarget>
);

// A frame is a rival if it declares what it buys and what it costs. That's
// the whole definition — a frame without a stated trade isn't an alternative
// to anything, it's a step.
const isRival = (frame: Frame) => !!(frame.optimises || frame.costs);

// Rivals: the sketches line up first so they can be read against each other —
// the comparison is the whole point of the mode — then each one gets a full
// row beside its brief. Briefs used to sit directly under their own frame,
// which forced the writing into a narrow column and made the section read
// nothing like the rest of the page.
const Rivals = ({
  frames,
  screen,
  size,
  path,
}: {
  frames: Frame[];
  screen: ScreenVariant;
  size: FrameSize;
  path: string;
}) => (
  <Stack spacing={5}>
    <Box sx={{ pb: 1, overflowX: 'auto' }}>
      <Stack sx={{ alignItems: 'flex-start' }} direction='row' spacing={4}>
        {frames.map((frame, i) => (
          <Fragment key={frame.id}>
            {/* An 'or' between two consecutive frames that both declare a
                trade: without it a strip of six reads as six steps, and the
                two that are actually alternatives don't stand out. Derived
                from optimises/costs rather than authored, so a section can't
                claim a fork it hasn't stated the cost of. */}
            {i > 0 && isRival(frames[i - 1]) && isRival(frame) && (
              <Stack sx={{ alignSelf: 'center', flexShrink: 0 }}>
                <Chip label='or' />
              </Stack>
            )}
            <FrameSketch
              frame={frame}
              screen={screen}
              label={`${frame.id} · ${frame.name}`}
              size={size}
              path={`${path}.frames[${i}]`}
            />
          </Fragment>
        ))}
      </Stack>
    </Box>

    <Divider />

    <Stack spacing={5} divider={<Divider />}>
      {frames.map((frame, i) =>
        frame.flowOnly ? null : (
          <Stack
            sx={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}
            key={frame.id}
            direction='row'
            useFlexGap
          >
            <FrameSketch
              frame={frame}
              screen={screen}
              label={frame.id}
              size={size}
              path={`${path}.frames[${i}]`}
            />
            <FrameBrief
              frame={frame}
              path={`${path}.frames[${i}]`}
              size={size}
            />
          </Stack>
        ),
      )}
    </Stack>
  </Stack>
);

// Sequence: a strip first — the whole journey at a glance, which is what gets
// walked through in the room — then the detail as full rows, since six deep
// briefs are unreadable side by side. The strip renders one size down so it
// stays scannable while still responding to the control.
const Sequence = ({
  frames,
  screen,
  size,
  path,
}: {
  frames: Frame[];
  screen: ScreenVariant;
  size: FrameSize;
  path: string;
}) => {
  const stripSize = oneStepDown[size];
  // Asides are consequences of the journey, not steps in it — they'd put a
  // false arrow in the strip, so they only appear in the detail rows below.
  const flow = frames.filter((f) => !f.aside);
  return (
    <Stack spacing={5}>
      <Box sx={{ pb: 1, overflowX: 'auto' }}>
        <Stack sx={{ alignItems: 'flex-start' }} direction='row' spacing={2}>
          {flow.map((frame, i) => (
            <Fragment key={frame.id}>
              {i > 0 && (
                <Stack
                  sx={{
                    alignItems: 'center',
                    pt: screen === 'desktop' ? 14 : 20,
                    width: 88,
                    flexShrink: 0,
                  }}
                  spacing={0.5}
                >
                  <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                    <Icon icon={'long-arrow-right' as never} fontSize={20} />
                  </Box>
                  {frame.tap && (
                    <Typography
                      sx={{ textAlign: 'center' }}
                      variant='caption'
                      color='text.secondary'
                    >
                      {frame.tap}
                    </Typography>
                  )}
                </Stack>
              )}
              <FrameSketch
                frame={frame}
                screen={screen}
                label={`${frame.id} · ${frame.name}`}
                size={stripSize}
                path={`${path}.frames[${frames.indexOf(frame)}]`}
              />
            </Fragment>
          ))}
        </Stack>
      </Box>

      {/* A section can be strip-only — every frame flowOnly, because each one
          is argued in its own section further down. Then there are no detail
          rows, and the divider would be a rule under nothing. */}
      {frames.some((f) => !f.flowOnly) && (
        <>
          <Divider />

          <Stack spacing={5} divider={<Divider />}>
            {frames.map((frame, i) =>
              frame.flowOnly ? null : (
                <Stack
                  sx={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}
                  key={frame.id}
                  direction='row'
                  useFlexGap
                >
                  <FrameSketch
                    frame={frame}
                    screen={screen}
                    label={frame.id}
                    caption={frame.tap}
                    size={size}
                    path={`${path}.frames[${i}]`}
                  />
                  <FrameBrief
                    frame={frame}
                    path={`${path}.frames[${i}]`}
                    size={size}
                  />
                </Stack>
              ),
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
};

// Probes: independent sketches, each with its brief beside it.
const Probes = ({
  frames,
  screen,
  size,
  path,
}: {
  frames: Frame[];
  screen: ScreenVariant;
  size: FrameSize;
  path: string;
}) => (
  <Stack spacing={5} divider={<Divider />}>
    {frames.map((frame, i) => (
      <Stack
        sx={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}
        key={frame.id}
        direction='row'
        useFlexGap
      >
        <FrameSketch
          frame={frame}
          screen={screen}
          label={frame.id}
          size={size}
          path={`${path}.frames[${i}]`}
        />
        <FrameBrief frame={frame} path={`${path}.frames[${i}]`} size={size} />
      </Stack>
    ))}
  </Stack>
);

// The anchor a jump-menu entry scrolls to. Derived from the section id so the
// two can't fall out of step.
const sectionAnchor = (id: string) => `explore-section-${id}`;

// Which section is being read. Tracked on scroll rather than with an
// IntersectionObserver because frames lazy-mount as you go (ExploreScreen's
// LazyMount), so section heights change under you and fixed observer margins
// drift. Reading the rects on scroll is a handful of measurements and always
// current.
//
// The active section is the last one whose top has passed the reading line, so
// it changes as a heading reaches the top of the viewport rather than when the
// section first appears at the bottom.
const READING_LINE = 140;

const useActiveSection = (ids: string[]): string | undefined => {
  const key = ids.join('|');
  const [active, setActive] = useState<string | undefined>(ids[0]);

  useEffect(() => {
    const sections = key
      .split('|')
      .map((id) => [id, document.getElementById(sectionAnchor(id))] as const)
      .filter((pair): pair is readonly [string, HTMLElement] => !!pair[1]);
    if (sections.length === 0) return;

    const recompute = () => {
      let current = sections[0][0];
      for (const [id, element] of sections) {
        if (element.getBoundingClientRect().top <= READING_LINE) current = id;
      }
      setActive(current);
    };

    recompute();
    window.addEventListener('scroll', recompute, { passive: true });
    window.addEventListener('resize', recompute);
    return () => {
      window.removeEventListener('scroll', recompute);
      window.removeEventListener('resize', recompute);
    };
  }, [key]);

  return active;
};

// One section, as a jump entry.
const SectionJump = ({
  section,
  isActive,
  isIndented,
}: {
  section: Section;
  isActive?: boolean;
  isIndented?: boolean;
}) => (
  <MenuItem
    selected={isActive}
    onClick={() =>
      document
        .getElementById(sectionAnchor(section.id))
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    // Section titles are sentences, not labels, so they have to wrap —
    // MenuItem's default nowrap would clip half of them.
    sx={{ whiteSpace: 'normal', borderRadius: '8px', pl: isIndented ? 3 : 2 }}
  >
    <ListItemText
      slotProps={{
        primary: {
          variant: 'body2',
          sx: { lineHeight: 1.4, fontWeight: isActive ? 600 : 400 },
        },
      }}
      primary={section.title}
    />
  </MenuItem>
);

// The project menu, and the reason this page has one. A project's pages are the
// parent level: clicking one changes what is on the right, and the page you are
// on opens to its sections, which move you around within it. Only the open page
// lists sections, because they scroll this page and would point at nothing from
// any other.
//
// Sticky within the sections block rather than the page, so it only exists while
// there is something to move between — above the divider there are no sections,
// and a nav pointing at nothing is furniture.
const ProjectNav = ({
  spec,
  onOpenPage,
}: {
  spec: ExplorationSpec;
  onOpenPage: (path: string) => void;
}) => {
  const project = projectForSpec(spec);
  const activeSection = useActiveSection(spec.sections.map((s) => s.id));

  return (
    <Stack
      sx={{ width: 200, flexShrink: 0, position: 'sticky', top: 24 }}
      spacing={1}
    >
      <SectionTitle
        label={project ? project.title.toUpperCase() : 'SECTIONS'}
      />
      <MenuList dense disablePadding>
        {/* A standalone exploration is its own project of one: no parent level,
            just its sections, exactly as before. */}
        {project === undefined
          ? spec.sections.map((section) => (
              <SectionJump
                key={section.id}
                section={section}
                isActive={section.id === activeSection}
              />
            ))
          : project.pages.map((page) => {
              const isOpen = page.spec === spec;
              return (
                <Fragment key={page.spec.id}>
                  <MenuItem
                    selected={isOpen}
                    onClick={() => onOpenPage(explorationRoute(page.spec))}
                    sx={{ borderRadius: '8px' }}
                  >
                    <Stack
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                      direction='row'
                      spacing={1}
                    >
                      <Typography variant='body2'>{page.label}</Typography>
                      <Box sx={{ display: 'flex', color: 'text.disabled' }}>
                        <Icon
                          icon={
                            (isOpen ? 'chevron-down' : 'chevron-right') as never
                          }
                          fontSize={9}
                        />
                      </Box>
                    </Stack>
                  </MenuItem>
                  {isOpen &&
                    page.spec.sections.map((section) => (
                      <SectionJump
                        key={section.id}
                        section={section}
                        isActive={section.id === activeSection}
                        isIndented
                      />
                    ))}
                </Fragment>
              );
            })}
      </MenuList>
    </Stack>
  );
};

const SectionBlock = ({
  section,
  screen,
  size,
  path,
}: {
  section: Section;
  screen: ScreenVariant;
  size: FrameSize;
  path: string;
}) => (
  <Card id={sectionAnchor(section.id)} sx={{ p: 3, scrollMarginTop: 24 }}>
    <Stack spacing={4}>
      {/* A bare section keeps its title for the jump menu but doesn't print it:
          with one frame, the frame's own brief already gives the name and the
          description, and a header above it says everything twice. */}
      {!section.bare && (
        <>
          <Stack spacing={1.5}>
            <EditableText
              variant='h2'
              path={`${path}.title`}
              what={`Section "${section.title}" — title`}
              value={section.title}
            />

            {section.intent && (
              <EditableText
                sx={{ maxWidth: 760 }}
                variant='body1'
                color='text.secondary'
                path={`${path}.intent`}
                what={`Section "${section.title}" — intent`}
                value={section.intent}
              />
            )}
          </Stack>

          <Divider />
        </>
      )}

      {section.mode === 'rivals' && (
        <Rivals
          frames={section.frames}
          screen={screen}
          size={size}
          path={path}
        />
      )}
      {section.mode === 'sequence' && (
        <Sequence
          frames={section.frames}
          screen={screen}
          size={size}
          path={path}
        />
      )}
      {section.mode === 'probes' && (
        <Probes
          frames={section.frames}
          screen={screen}
          size={size}
          path={path}
        />
      )}
    </Stack>
  </Card>
);

// An exploration: one open design question rendered as frames to argue with.
// The divergent counterpart to HandoverPage — same spec-file-not-a-page
// authoring model, opposite intent. Nothing here is a proposal, and the page
// says so before it shows anything.
const ExplorationPage = ({ spec }: { spec: ExplorationSpec }) => {
  const concept = useContext(ConceptTag);
  // Switching pages within a project is a route change, so the page navigates
  // for itself rather than taking a callback — it is rendered by a Route, the
  // same way About reads its section from the URL.
  const navigate = useNavigate();
  const screen: ScreenVariant = spec.screen ?? 'phone';
  // Large by default: an exploration is read and argued with on a shared
  // screen, so thumbnail scale is the wrong starting point. A spec can start
  // fuller — desktop frames carry tables and small type, which large still
  // shrinks past reading.
  const [size, setSize] = useState<FrameSize>(spec.size ?? 'large');
  // View by default. An exploration spends most of its life being read or
  // walked through, and the editing affordances make a page of sketches feel
  // like a tool you might break.
  const [editing, setEditing] = useState(false);
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);
  const total = spec.sections.reduce((n, s) => n + s.frames.length, 0);
  const sketched = spec.sections.reduce(
    (n, s) => n + s.frames.filter((f) => f.render).length,
    0,
  );

  return (
    <ThemeProvider theme={workSansHeadings}>
      <EditingProvider sourceFile={spec.sourceFile} enabled={editing}>
        <Box sx={{ px: 4, py: 5 }}>
          <Stack
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 4,
            }}
            direction='row'
            spacing={2}
          >
            <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
              <Typography variant='overline' color='text.secondary'>
                Exploration
              </Typography>
              <Typography variant='overline' color='text.secondary'>
                /
              </Typography>
              <Typography variant='overline'>{spec.title}</Typography>
              {/* Which concept, as on a flows page. */}
              {concept && (
                <>
                  <Typography variant='overline' color='text.secondary'>
                    /
                  </Typography>
                  <Typography variant='overline'>{concept}</Typography>
                </>
              )}
            </Stack>

            <Stack sx={{ alignItems: 'center' }} direction='row' spacing={2}>
              <ToggleButtonGroup
                size='small'
                exclusive
                value={size}
                onChange={(_, next: FrameSize | null) => next && setSize(next)}
                aria-label='Frame size'
              >
                {(['compact', 'large', 'full'] as FrameSize[]).map((s) => (
                  <ToggleButton key={s} value={s}>
                    {SIZE_LABEL[s]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              <ToggleButtonGroup
                size='small'
                exclusive
                value={editing}
                onChange={(_, next: boolean | null) =>
                  next !== null && setEditing(next)
                }
                aria-label='Mode'
              >
                <ToggleButton value={false}>View</ToggleButton>
                <ToggleButton value={true}>Edit</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>

          <Stack
            sx={{ alignItems: 'flex-start' }}
            direction='row'
            spacing={6}
            divider={<Divider orientation='vertical' flexItem />}
          >
            <Stack sx={{ flex: 1, minWidth: 0 }} spacing={4}>
              <Stack spacing={1.5}>
                <EditableText
                  variant='h1'
                  path='title'
                  what='Exploration title'
                  value={spec.title}
                />
                <EditableText
                  variant='body1'
                  color='text.secondary'
                  path='stance'
                  what='The divergence stance, shown under the title'
                  value={spec.stance}
                />
              </Stack>

              {spec.readingNotes && spec.readingNotes.length > 0 && (
                <Stack spacing={2}>
                  <SectionTitle label='HOW TO READ THIS' />
                  <Stack spacing={1.5}>
                    {spec.readingNotes.map((note, i) => (
                      <EditableText
                        key={i}
                        variant='body2'
                        color='text.secondary'
                        path={`readingNotes[${i}]`}
                        what={`How to read this — note ${i + 1}`}
                        value={note}
                        sx={{
                          position: 'relative',
                          pl: 3,
                          // Hanging bullet, matching the handover page: every dot
                          // sits in the same gutter and wrapped lines align under
                          // the text rather than the marker.
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: '2px',
                            top: '0.62em',
                            width: 5,
                            height: 5,
                            borderRadius: '50%',
                            backgroundColor: 'text.secondary',
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Stack>
              )}
            </Stack>

            <Stack
              sx={{ width: 360, flexShrink: 0 }}
              spacing={4}
              divider={<Divider />}
            >
              <Stack spacing={1.5}>
                <SectionTitle label='STATUS' />
                <Box>
                  <Chip
                    size='small'
                    label={spec.verdict ? 'Resolved' : 'Open — nothing decided'}
                    color={spec.verdict ? 'limeGreen' : 'warmOrange'}
                  />
                </Box>
                <Typography variant='body2' color='text.secondary'>
                  {sketched} of {total} frames sketched
                </Typography>
              </Stack>

              <Stack spacing={1.5}>
                <SectionTitle label='BY' />
                <Typography variant='body2'>{spec.by}</Typography>
              </Stack>

              <Stack spacing={1.5}>
                <SectionTitle label='FOR' />
                <Typography variant='body2' color='text.secondary'>
                  {spec.occasion}
                </Typography>
              </Stack>

              {spec.verdict && (
                <Stack spacing={1.5}>
                  <SectionTitle label='VERDICT' />
                  <Typography variant='body2'>
                    <Typography
                      component='span'
                      variant='inherit'
                      color='text.secondary'
                    >
                      Chose {spec.verdict.chosen} ·{' '}
                    </Typography>
                    {spec.verdict.rationale}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>

          <Divider sx={{ my: 5 }} />

          {/* The jump menu starts here rather than beside the intro: it is for
              moving between sections, so it has nothing to point at until the
              sections begin. */}
          <Stack sx={{ alignItems: 'flex-start' }} direction='row' spacing={5}>
            <ProjectNav spec={spec} onOpenPage={(path) => navigate(path)} />

            <Stack sx={{ flex: 1, minWidth: 0 }} spacing={5}>
              {spec.sections.map((section, i) => (
                <SectionBlock
                  key={section.id}
                  section={section}
                  screen={screen}
                  size={size}
                  path={`sections[${i}]`}
                />
              ))}

              {(spec.walkthrough || spec.thread) && (
                <Card sx={{ p: 3 }}>
                  <Disclosure
                    label={
                      walkthroughOpen
                        ? 'Hide the running order'
                        : 'The running order, and the thread to pull on'
                    }
                    open={walkthroughOpen}
                    onToggle={() => setWalkthroughOpen(!walkthroughOpen)}
                  >
                    <Stack spacing={2.5}>
                      {spec.walkthrough && (
                        <Stack spacing={1.5}>
                          {spec.walkthrough.map((line, i) => (
                            <Stack
                              sx={{ alignItems: 'flex-start' }}
                              key={i}
                              direction='row'
                              spacing={2}
                            >
                              <Typography
                                sx={{ width: 20, flexShrink: 0 }}
                                variant='body2'
                                color='text.secondary'
                              >
                                {i + 1}
                              </Typography>
                              <EditableText
                                variant='body2'
                                path={`walkthrough[${i}]`}
                                what={`Running order — step ${i + 1}`}
                                value={line}
                              />
                            </Stack>
                          ))}
                        </Stack>
                      )}

                      {spec.thread && (
                        <Box
                          sx={{
                            borderLeft: 2,
                            borderColor: 'custom.coral.600',
                            pl: 2,
                          }}
                        >
                          <EditableText
                            variant='body2'
                            path='thread'
                            what='The thread to pull on'
                            value={spec.thread}
                          />
                        </Box>
                      )}
                    </Stack>
                  </Disclosure>
                </Card>
              )}
            </Stack>
          </Stack>
        </Box>
      </EditingProvider>
    </ThemeProvider>
  );
};

export default ExplorationPage;
